import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../lib/dateFmt';
import {
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  ClipboardCheck,
  FileText,
  LogOut,
  Mail,
  Pencil,
  User as UserIcon,
  X,
} from 'lucide-react';
import type { Station } from '@hcc/types';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { FlagsButton } from './FlagsButton';
import type { PersonApi } from '../../hooks/usePeople';
import { api, ApiError } from '../../lib/api';

const STATION_LABELS: Record<Station, string> = {
  IT: 'IT',
  PPE: 'PPE / Uniform',
  Tools: 'Tools',
  Fleet: 'Fleet',
  DBSHR: 'DBS / HR',
};

type PreflightStatus = 'ok' | 'pending' | 'missing' | 'na';

interface Preflight {
  dbs_status: PreflightStatus;
  right_to_work: PreflightStatus;
  dvla_check: PreflightStatus;
  p45_received: PreflightStatus;
  notes?: string;
  updated_at?: string;
}

const PREFLIGHT_FIELDS: {
  key: keyof Pick<Preflight, 'dbs_status' | 'right_to_work' | 'dvla_check' | 'p45_received'>;
  label: string;
}[] = [
  { key: 'dbs_status', label: 'DBS' },
  { key: 'right_to_work', label: 'Right to work' },
  { key: 'dvla_check', label: 'DVLA' },
  { key: 'p45_received', label: 'P45 / HMRC' },
];

const PREFLIGHT_TONE: Record<
  PreflightStatus,
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  ok: 'success',
  pending: 'warning',
  missing: 'danger',
  na: 'neutral',
};

const PREFLIGHT_LABEL: Record<PreflightStatus, string> = {
  ok: 'OK',
  pending: 'Pending',
  missing: 'Missing',
  na: 'N/A',
};

/**
 * Read-only details modal for a person, surfaced from the people-admin
 * list via the new Info button. Renders everything that's already on
 * the list row (so no extra fetch) plus arrival status and per-station
 * completion. The Edit button at the bottom routes to /admin/people/:id
 * for anything that needs changing.
 */
export function PersonInfoModal({
  person,
  onClose,
}: {
  person: PersonApi;
  onClose: () => void;
}) {
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(true);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Lazy-fetch the pre-employment checklist when the modal opens. The
  // list endpoint doesn't ship metadata, and we don't want to bloat
  // every people-list response with the per-record metadata blob.
  useEffect(() => {
    let cancelled = false;
    setPreflightLoading(true);
    setPreflightError(null);
    (async () => {
      try {
        const r = await api<{ metadata: { preflight?: Preflight } }>(
          `/api/admin/people/${person.id}`,
        );
        if (cancelled) return;
        setPreflight(r.metadata?.preflight ?? null);
      } catch (err) {
        if (cancelled) return;
        setPreflightError(
          err instanceof ApiError ? err.message : 'could not load the checklist',
        );
      } finally {
        if (!cancelled) setPreflightLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [person.id]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-fg/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div className="aurora-fade-in flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="flex items-start justify-between gap-3 border-b border-divider/10 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-token-sm bg-accent/10 text-accent">
              <UserIcon className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-semibold tracking-tight text-fg">
                  {person.full_name}
                </h2>
                <FlagsButton
                  personId={person.id}
                  personName={person.full_name}
                  count={person.active_flags ?? 0}
                />
                <ArrivalChip
                  arrived={person.arrived}
                  arrivedAt={person.arrived_at ?? null}
                />
                {person.left_early ? (
                  <Pill
                    tone="warning"
                    size="sm"
                    icon={<LogOut className="h-3 w-3" />}
                  >
                    Left early
                  </Pill>
                ) : null}
              </div>
              <p className="truncate text-xs text-fg-dim">
                {person.position ?? 'Position unknown'}
                {person.contract ? ` · ${person.contract}` : ''}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Detail icon={<Mail className="h-3.5 w-3.5" />} label="Email">
              <a href={`mailto:${person.email}`} className="hover:underline">
                {person.email}
              </a>
            </Detail>
            <Detail label="Employee ID">
              {person.employee_id ?? <em className="text-fg-dim">—</em>}
            </Detail>
            <Detail label="Manager">
              {person.manager ?? <em className="text-fg-dim">—</em>}
            </Detail>
            <Detail icon={<CalendarDays className="h-3.5 w-3.5" />} label="Start date">
              {person.start_date ?? <em className="text-fg-dim">—</em>}
            </Detail>
          </dl>

          {person.notes ? (
            <div className="mt-4 rounded-token-sm border border-line/10 bg-surface-2 p-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-dim">
                Notes
              </div>
              <p className="whitespace-pre-wrap text-sm text-fg">
                {person.notes}
              </p>
            </div>
          ) : null}

          <div className="mt-4">
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-fg-dim">
              <ClipboardCheck className="h-3 w-3" />
              Pre-Employment Checklist
            </div>
            {preflightLoading ? (
              <div className="text-xs text-fg-dim">Loading…</div>
            ) : preflightError ? (
              <div className="text-xs text-danger-soft-fg">{preflightError}</div>
            ) : !preflight ? (
              <div className="text-xs text-fg-muted">
                Not started yet. Use <em>Edit person</em> to fill it in.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {PREFLIGHT_FIELDS.map((f) => {
                    // Older records predate some gates (e.g. DVLA); treat
                    // an absent field as pending, same as the API does.
                    const v = preflight[f.key] ?? 'pending';
                    return (
                      <Pill key={f.key} tone={PREFLIGHT_TONE[v]} size="sm">
                        <span className="font-semibold uppercase tracking-wider text-[10px]">
                          {f.label}
                        </span>
                        <span className="opacity-60">·</span>
                        <span>{PREFLIGHT_LABEL[v]}</span>
                      </Pill>
                    );
                  })}
                </div>
                {preflight.notes ? (
                  <div className="mt-2 rounded-token-sm border border-line/10 bg-surface-2 px-2.5 py-1.5 text-xs text-fg">
                    <span className="font-medium text-fg-muted">Note:</span>{' '}
                    {preflight.notes}
                  </div>
                ) : null}
                {preflight.updated_at ? (
                  <div className="mt-1 text-[10px] text-fg-dim">
                    Last updated{' '}
                    {formatDateTime(preflight.updated_at)}
                  </div>
                ) : null}
              </>
            )}
          </div>

          {person.stations ? (
            <div className="mt-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-fg-dim">
                Stations completed
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(person.stations) as Station[]).map((s) => {
                  const entry = person.stations![s];
                  const done = entry?.complete ?? false;
                  const eventId = entry?.latest_kit_issued_event_id ?? null;
                  if (done && eventId) {
                    return (
                      <a
                        key={s}
                        href={`/api/admin/kit-receipts/${eventId}/pdf?person_id=${person.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-full bg-success-soft/15 px-2 py-0.5 text-[11px] font-medium text-success-soft-fg transition-colors hover:bg-success-soft/25"
                        title={`View ${STATION_LABELS[s]} receipt PDF`}
                      >
                        <FileText className="h-3 w-3" />
                        {STATION_LABELS[s]}
                      </a>
                    );
                  }
                  return (
                    <Pill
                      key={s}
                      tone={done ? 'success' : 'neutral'}
                      size="sm"
                      icon={
                        done ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : (
                          <CircleDashed className="h-3 w-3" />
                        )
                      }
                    >
                      {STATION_LABELS[s]}
                    </Pill>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          <Link to={`/admin/people/${person.id}`}>
            <Button variant="primary" size="sm" iconLeft={<Pencil className="h-3.5 w-3.5" />}>
              Edit person
            </Button>
          </Link>
        </footer>
      </div>
    </div>
  );
}

function Detail({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-fg-dim">
        {icon}
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-fg">{children}</dd>
    </div>
  );
}

function ArrivalChip({
  arrived,
  arrivedAt,
}: {
  arrived?: boolean;
  arrivedAt: string | null;
}) {
  if (arrived) {
    return (
      <Pill
        tone="success"
        size="sm"
        icon={<CheckCircle2 className="h-3 w-3" />}
        className={arrivedAt ? '' : ''}
      >
        On site
      </Pill>
    );
  }
  return (
    <Pill tone="neutral" size="sm" icon={<CircleDashed className="h-3 w-3" />}>
      Not arrived
    </Pill>
  );
}
